import type { BudgetRowValues } from '@/components/ModifyBudgetRow/ModifyBudgetRow';
import type { OptimisationBase } from '@/optimizer';
import type { OptimizerServiceManagerReturn } from '@/optimizer/createOptimizerServiceManager';
import type { Message } from '@analytical-alley/opt-wasm';

export type ChannelInput = BudgetRowValues;
export type OptimizedPlan = Awaited<ReturnType<OptimizerServiceManagerReturn['getOptimizedPlan']>>;
export type MediaLimit = { medium: string; lowerLimit: number; upperLimit: number };

export type OptimisationInputs = {
    budget: number;
    optimizationBase: OptimisationBase;
    startDate: string;
    endDate: string;
    channels: Record<string, ChannelInput>;
};

export type OptimisationRun = {
    inputs: OptimisationInputs;
    result: OptimizedPlan;
    messages: Message[];
    finishedAt: number;
};

export type SessionStatus = 'bootstrapping' | 'idle' | 'running' | 'error';

export type SessionState = {
    status: SessionStatus;
    draft: OptimisationInputs | null;
    committed: OptimisationRun | null;
    error: Error | null;
    generation: number;
};

export type SessionAction =
    | { type: 'BOOTSTRAPPED'; inputs: OptimisationInputs }
    | { type: 'BOOTSTRAP_FAILED'; error: Error }
    | { type: 'EDIT'; patch: Partial<Pick<OptimisationInputs, 'budget' | 'optimizationBase' | 'startDate' | 'endDate'>> }
    | { type: 'SET_CHANNEL'; channel: string; value: ChannelInput }
    | { type: 'RUN_STARTED'; generation: number }
    | { type: 'RUN_SUCCEEDED'; generation: number; run: OptimisationRun }
    | { type: 'RUN_FAILED'; generation: number; error: Error }
    | { type: 'RESET_DRAFT' }
    | { type: 'DISMISS_ERROR' };

export const initialSessionState: SessionState = {
    status: 'bootstrapping',
    draft: null,
    committed: null,
    error: null,
    generation: 0,
};

const UNLOCKED: ChannelInput = { locked: false, investment: null };

const channelsEqual = (a: Record<string, ChannelInput>, b: Record<string, ChannelInput>): boolean => {
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
        const x = a[key] ?? UNLOCKED;
        const y = b[key] ?? UNLOCKED;
        if (x.locked !== y.locked) return false;
        if (!!x.excluded !== !!y.excluded) return false;
        if (x.locked && Number(x.investment) !== Number(y.investment)) return false;
    }
    return true;
};

export const inputsEqual = (a: OptimisationInputs, b: OptimisationInputs): boolean =>
    a.budget === b.budget &&
    a.optimizationBase === b.optimizationBase &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    channelsEqual(a.channels, b.channels);

export type OptimalVariable = { optimalSpend: number; minSpend: number; maxSpend: number; kpi: number };

export const reconcileInputs = (sent: OptimisationInputs, result: OptimizedPlan): OptimisationInputs => {
    const channels: Record<string, ChannelInput> = { ...sent.channels };
    for (const [channel, optimal] of Object.entries(result.optimalVariables as Record<string, OptimalVariable>)) {
        const previous = sent.channels[channel];
        if (previous?.locked) continue;
        channels[channel] = { locked: false, excluded: previous?.excluded, investment: optimal.optimalSpend };
    }
    return { ...sent, budget: result.budget ?? sent.budget, channels };
};

export const sessionReducer = (state: SessionState, action: SessionAction): SessionState => {
    switch (action.type) {
        case 'BOOTSTRAPPED':
            if (state.status !== 'bootstrapping') return state;
            return { ...state, status: 'idle', draft: action.inputs };
        case 'BOOTSTRAP_FAILED':
            if (state.status !== 'bootstrapping') return state;
            return { ...state, status: 'error', error: action.error };
        case 'EDIT':
            if (!state.draft) return state;
            return { ...state, draft: { ...state.draft, ...action.patch } };
        case 'SET_CHANNEL':
            if (!state.draft) return state;
            return {
                ...state,
                draft: { ...state.draft, channels: { ...state.draft.channels, [action.channel]: action.value } },
            };
        case 'RUN_STARTED':
            if (state.status === 'running' || !state.draft) return state;
            return { ...state, status: 'running', generation: action.generation, error: null };
        case 'RUN_SUCCEEDED':
            if (state.status !== 'running' || action.generation !== state.generation) return state;
            return { ...state, status: 'idle', committed: action.run, draft: action.run.inputs, error: null };
        case 'RUN_FAILED':
            if (state.status !== 'running' || action.generation !== state.generation) return state;
            return { ...state, status: 'error', error: action.error };
        case 'RESET_DRAFT':
            if (!state.committed) return state;
            return { ...state, draft: state.committed.inputs };
        case 'DISMISS_ERROR':
            if (state.status !== 'error' || state.draft === null) return state;
            return { ...state, status: 'idle', error: null };
        default:
            return state;
    }
};

export type SessionFlags = { isDirty: boolean; isRunning: boolean; canRun: boolean; isInitialLoading: boolean };

export const deriveFlags = (state: SessionState, canEdit: boolean): SessionFlags => {
    const isRunning = state.status === 'running';
    const isDirty = !!state.draft && !!state.committed && !inputsEqual(state.draft, state.committed.inputs);
    return { isDirty, isRunning, canRun: isDirty && !isRunning && canEdit, isInitialLoading: state.committed === null };
};
